import {
  CheckIcon,
  ChevronDownIcon,
  Copy06Icon,
  FilterLinesIcon,
  InfoCircleIcon,
  Key01Icon,
} from '@langchain/untitled-ui-icons';

import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { EvaluatorSecrets } from '@/Pages/Settings/components/EvaluatorSecrets';
import { Button, IconButton, Text, Tooltip } from '@/design-system';
import {
  EProviderSecretName,
  useHasWorkspaceSecrets,
} from '@/hooks/useHasWorkspaceSecrets';
import {
  InstanceFlags,
  useInstanceFlagOrDefault,
} from '@/hooks/useInstanceFlag';
import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import {
  useCreateFilterViewMutation,
  useGetFilterView,
  useRunStats,
  useUpdateFilterViewMutation,
} from '@/hooks/useSwr';
import {
  FilterViewSchema,
  FilterViewType,
  RunSchema,
  RunStatsSchema,
} from '@/types/schema';
import { xCount } from '@/utils/stringUtils';
import { cn } from '@/utils/tailwind';

import { CopyIconButton } from '../CopyButton/CopyButton';
import { useCopyAction } from '../CopyButton/useCopy';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../Dropdown';
import { ExpandableErrorAlert } from '../ExpandableErrorAlert';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import {
  astToString,
  findComparsion,
  flattenAst,
  stringToAst,
} from '../RunsTable/filter/ast';
import { useFilterBarOpen } from '../RunsTable/hooks/useFilterBarOpen';
import { SearchModel } from '../RunsTable/types';
import { Switch } from '../Switch/Switch';
import {
  getActiveFieldsCount,
  normalizeFilterString,
  useFilterAst,
} from './FilterBar.utils';
import { AddFilterButton } from './components/AddFilterButton';
import { ClearFilterButton } from './components/DeleteFilterButton';
import {
  SaveFilterForm,
  SaveFilterFormData,
} from './components/EditFilterButton';
import { FilterBarGeneratedInput } from './components/FilterBarGeneratedInput';
import { FilterBarOperandsV2 } from './components/FilterBarOperands';
import { FilterBarRawInput } from './components/FilterBarRawInput';
import { FilterLabel } from './constants';
import { getRunFields } from './fields/runs';

const pluralRules = new Intl.PluralRules('en-US');

export function SaveFilterPopover(props: {
  filter?: string;
  traceFilter?: string;
  treeFilter?: string;
  filterView?: FilterViewSchema;
  onViewChange?: (
    view: 'llm' | 'trace' | 'errors' | string,
    maybeFilter?: string,
    maybeTraceFilter?: string,
    maybeTreeFilter?: string
  ) => void;
  isLoading: boolean;
  saveFilterType: FilterViewType;
  className?: string;
  getSideOffset?: (showSaveForm: boolean) => number;
}) {
  const { sessionId } = useParams();

  const [isOpen, setIsOpen] = useState(false);
  const [showSaveForm, setShowSaveForm] = useState(false);

  const createFilter = useCreateFilterViewMutation(sessionId);

  const updateFilter = useUpdateFilterViewMutation(
    sessionId,
    props.filterView?.id
  );

  const hasChange =
    props.filterView &&
    (normalizeFilterString(props.filter) !==
      normalizeFilterString(props.filterView.filter_string) ||
      normalizeFilterString(props.traceFilter) !==
        normalizeFilterString(props.filterView.trace_filter_string) ||
      normalizeFilterString(props.treeFilter) !==
        normalizeFilterString(props.filterView.tree_filter_string));

  const handleSave = () => {
    if (
      sessionId &&
      (props.filter || props.traceFilter || props.treeFilter) &&
      props.filterView?.id
    ) {
      updateFilter.trigger({
        json: {
          filter_string: props.filter,
          trace_filter_string: props.traceFilter,
          tree_filter_string: props.treeFilter,
        },
      });
      setIsOpen(false);
    }
  };
  const handleCreate = async ({ name, description }: SaveFilterFormData) => {
    if (sessionId && (props.filter || props.traceFilter || props.treeFilter)) {
      const resp = await createFilter.trigger({
        json: {
          display_name: name,
          description,
          filter_string: props.filter,
          trace_filter_string: props.traceFilter,
          tree_filter_string: props.treeFilter,
          type: props.saveFilterType,
        },
      });
      props.onViewChange?.(
        resp?.id ?? '',
        resp?.filter_string,
        resp?.trace_filter_string,
        resp?.tree_filter_string
      );
      setIsOpen(false);
      setShowSaveForm(false);
    }
  };

  const handleSaveAs = () => {
    setShowSaveForm(true);
  };

  if (!hasChange && (props.filterView || props.isLoading)) return null;

  const sideOffset = props.getSideOffset?.(showSaveForm);

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) {
          setShowSaveForm(false);
        }
      }}
    >
      <PopoverTrigger
        className={cn(
          'flex items-center justify-center gap-1 whitespace-nowrap rounded-md',
          'border border-secondary px-2 py-0.5 text-sm transition-all hover:bg-secondary',
          showSaveForm &&
            'cursor-auto bg-secondary text-tertiary hover:bg-secondary-hover',
          props.className
        )}
      >
        {props.filterView ? 'Update filter' : 'Save filter'}
        {props.filterView && <ChevronDownIcon className={cn('h-4 w-4')} />}
      </PopoverTrigger>
      <PopoverContent
        side={props.filterView && !showSaveForm ? 'bottom' : 'right'}
        align={props.filterView && 'start'}
        sideOffset={
          props.filterView && (sideOffset ?? (!showSaveForm ? 4 : 16))
        }
        className={cn(
          {
            'p-0': props.filterView && !showSaveForm,
            'bg-popover': showSaveForm,
          },
          'w-fit'
        )}
      >
        {props.filterView && !showSaveForm ? (
          <div className="w-[100px]">
            <button
              type="button"
              onClick={handleSave}
              className="w-full px-2 py-1 text-left text-xxs hover:bg-secondary-hover focus:outline-none"
            >
              Update
            </button>
            <button
              type="button"
              onClick={handleSaveAs}
              className="w-full px-2 py-1 text-left text-xxs hover:bg-secondary-hover focus:outline-none"
            >
              Create new
            </button>
          </div>
        ) : (
          <SaveFilterForm
            saveAs={showSaveForm}
            submitting={createFilter.isMutating}
            onSubmit={handleCreate}
            onCancel={() => setIsOpen(false)}
            error={createFilter.error?.message}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}

interface FilterDialogContentProps {
  stats: RunStatsSchema | undefined;
  value: SearchModel;
  onChange: (value: SearchModel) => void;
  simpleFiltersOnly?: boolean;
  setFeedbackUrls?: (urls: Record<string, string>) => void;
  onViewChange?: (
    view: 'llm' | 'trace' | 'errors' | string,
    maybeFilter?: string,
    maybeTraceFilter?: string,
    maybeTreeFilter?: string
  ) => void;
  setFilterBarOpen: (open: boolean) => void;
  showSaveFilters: boolean;

  hideAIQuery?: boolean;
  hideFilterTitle?: boolean;
  updateEveryChange?: boolean;
  onExperimentalSearchToggle?: (enabled: boolean) => void;
  alertFilteringEnabled?: boolean;
  disabledAlertRunFields?: FilterLabel[];
  readOnly?: boolean;
  disableAutoExpand?: boolean;
}

export function FilterDialogContent(props: FilterDialogContentProps) {
  const { sessionId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterViewId = props.onViewChange
    ? searchParams.get('custom_filter_view_id')
    : undefined;
  const { data: filterView, isLoading: filterViewLoading } = useGetFilterView(
    sessionId,
    filterViewId ?? undefined
  );

  const { onCopy, copied } = useCopyAction();

  // NOTE searchEnabled is used to determine whether to show filtering fields
  // on sensitive information. This is turned OFF for hybrid-deployments
  // where sensitive information is not stored in ClickHouse.
  const searchEnabled = useInstanceFlagOrDefault(
    InstanceFlags.search_enabled
  ) as boolean;

  const { value: expSearchOrgConfigEnabled } = useOrgConfig(
    OrgConfigs.langsmith_experimental_search_enabled
  );

  const expSearchSupported = useInstanceFlagOrDefault(
    InstanceFlags.experimental_search_enabled
  ) as boolean;

  // expFilteringEnabledForTenantOrDeployment is used to determine whether to
  // show experimental filtering operands which populate the `searchFilter`
  // field in the search model.
  // This CAN be turned ON for hybrid-deployments.
  const expFilteringEnabledForTenantOrDeployment =
    expSearchSupported && (expSearchOrgConfigEnabled as boolean);

  const experimentalSearchEnabled = props.value.useExperimentalSearch ?? false;

  const runFields = getRunFields({
    searchEnabled: searchEnabled,
    experimentalSearchEnabled:
      expFilteringEnabledForTenantOrDeployment && experimentalSearchEnabled,
    alertFilteringEnabled: props.alertFilteringEnabled,
    disabledAlertRunFields: props.disabledAlertRunFields,
  });

  const [query, setQuery] = useFilterAst(props.value.filter, runFields);
  // Separate state for experimental search filter
  const [searchQuery, setSearchQuery] = useFilterAst(
    props.value.searchFilter,
    runFields
  );

  const generateAiQueryEnabled = useInstanceFlagOrDefault(
    InstanceFlags.generate_ai_query_enabled
  );
  const [traceQuery, setTraceQuery] = useFilterAst(
    props.value.traceFilter,
    runFields
  );
  const [treeQuery, setTreeQuery] = useFilterAst(
    props.value.treeFilter,
    runFields
  );

  const [showAdvanced, setShowAdvanced] = useState(
    () => traceQuery.length > 0 || treeQuery.length > 0
  );

  const [feedbackUrlsByOperand, setFeedbackUrlsByOperand] = useState<
    Record<string, Record<string, string>>
  >({});

  const queries = [
    props.value.filter,
    props.value.traceFilter,
    props.value.treeFilter,
  ].filter((x): x is string => x != null && !!x);

  const traceFilterEnabled = !query.some((group) =>
    group.comparisons.find(findComparsion('eq', 'is_root', true))
  );

  const hasDisabledFields = query.some(
    (group) => group.field && group.field.disabled?.({ runStats: props.stats })
  );

  const { openSecretsRef, missingSecrets } = useHasWorkspaceSecrets([
    EProviderSecretName.OPENAI,
  ]);

  const shouldRenderShowAdvancedButton =
    !experimentalSearchEnabled &&
    !(props.simpleFiltersOnly && props.hideAIQuery);

  const getShowAdvancedButtonText = () => {
    if (props.simpleFiltersOnly) {
      return showAdvanced ? 'Switch to AI Query' : 'Switch to Raw Query';
    }
    return showAdvanced ? 'Close advanced filters' : 'Advanced filters';
  };

  const { onChange: _onChange } = props;
  const onChange = useCallback(
    (value: SearchModel) => {
      // Clear custom_filter_view_id if filter has changed.
      // filterView is not cleared intentionally so user can still update the
      // filter if they need to but will not be able to next time this dialog is opened
      // beacuse saved filter is no longer selected.
      if (
        filterView &&
        (filterView.filter_string !== value.filter ||
          filterView.trace_filter_string !== value.traceFilter ||
          filterView.tree_filter_string !== value.treeFilter)
      ) {
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete('custom_filter_view_id');
          return newParams;
        });
      }
      _onChange(value);
    },
    [filterView, _onChange, setSearchParams]
  );

  // add debounce here to make filters more responsive
  // because changing filter can trigger rerender of the whole table
  const debouncedOnChange = useMemo(
    () =>
      debounce((value: SearchModel) => {
        onChange(value);
      }, 300),
    [onChange]
  );

  useEffect(() => {
    return () => {
      debouncedOnChange.cancel();
    };
  }, [debouncedOnChange]);

  const queryOrSearchQuery = experimentalSearchEnabled ? searchQuery : query;

  // Auto-expand Field dropdown when no filters are applied
  const displayQuery =
    queryOrSearchQuery.length === 0 && !props.disableAutoExpand
      ? [{ field: undefined, comparisons: [{}] }]
      : queryOrSearchQuery;

  return (
    <div className="group flex flex-col items-stretch border-tertiary text-sm">
      {props.hideFilterTitle ? null : (
        <div className="flex flex-col p-3 pb-2.5">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1 font-medium">
              {filterView?.display_name ?? 'Filters'}
              {filterView?.description && (
                <Tooltip title={filterView.description}>
                  <div>
                    <InfoCircleIcon className="h-4 w-4 text-tertiary" />
                  </div>
                </Tooltip>
              )}
            </span>
            <div className="flex items-center justify-center gap-1">
              {queryOrSearchQuery.length > 0 &&
                props.showSaveFilters &&
                !experimentalSearchEnabled && (
                  <SaveFilterPopover
                    filter={props.value.filter}
                    traceFilter={props.value.traceFilter}
                    treeFilter={props.value.treeFilter}
                    filterView={filterView}
                    onViewChange={props.onViewChange}
                    isLoading={filterViewLoading}
                    saveFilterType={FilterViewType.RUNS}
                    className="text-xxs"
                    getSideOffset={(showSaveForm) => (!showSaveForm ? 4 : 94)}
                  />
                )}

              {queries.length > 1 && !props.readOnly ? (
                <DropdownMenu>
                  <DropdownMenuTrigger className="flex h-6 w-6 items-center justify-center rounded-md border border-secondary p-0 outline-none">
                    {copied ? (
                      <CheckIcon className="h-4 w-4" />
                    ) : (
                      <Copy06Icon className="h-4 w-4" />
                    )}
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="text-xxs">
                    {props.value.filter && (
                      <DropdownMenuItem
                        onSelect={() => onCopy(props.value.filter ?? '')}
                      >
                        Copy filters
                      </DropdownMenuItem>
                    )}
                    {props.value.traceFilter && (
                      <DropdownMenuItem
                        onSelect={() => onCopy(props.value.traceFilter ?? '')}
                      >
                        Copy trace filters
                      </DropdownMenuItem>
                    )}
                    {props.value.treeFilter && (
                      <DropdownMenuItem
                        onSelect={() => onCopy(props.value.treeFilter ?? '')}
                      >
                        Copy tree filters
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : queryOrSearchQuery.length > 0 ? (
                !props.readOnly && (
                  <CopyIconButton
                    className="p-1 text-quaternary"
                    copy={queries.at(0) ?? ''}
                    copyText={
                      <span>
                        Copy the filter(s) to use when filtering via{' '}
                        <a
                          href="https://api.smith.langchain.com/redoc#tag/run/operation/query_runs_api_v1_runs_query_post"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline"
                        >
                          API
                        </a>{' '}
                        or{' '}
                        <a
                          href="https://docs.smith.langchain.com/reference/python/client/langsmith.client.Client#langsmith.client.Client.list_runs"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline"
                        >
                          SDK
                        </a>
                      </span>
                    }
                  />
                )
              ) : null}

              {!props.readOnly && (
                <ClearFilterButton
                  hasFilters={queryOrSearchQuery.length > 0}
                  onDeleteFilterValues={() => {
                    if (experimentalSearchEnabled) {
                      // Only clear search query in experimental mode
                      setSearchQuery([]);
                      onChange({
                        ...props.value,
                        searchFilter: undefined,
                      });
                    } else {
                      // Clear all queries in normal mode
                      setQuery([]);
                      setTraceQuery([]);
                      setTreeQuery([]);
                      onChange({
                        ...props.value,
                        filter: undefined,
                        traceFilter: undefined,
                        treeFilter: undefined,
                      });
                    }
                  }}
                  disabled={hasDisabledFields}
                />
              )}
            </div>
          </div>
        </div>
      )}
      <div
        className={cn(
          'flex flex-col gap-2',
          !props.hideFilterTitle ? 'px-3' : ''
        )}
      >
        <FilterBarOperandsV2
          fields={runFields}
          operandsWithFields={displayQuery}
          handleChange={(value) => {
            if (experimentalSearchEnabled) {
              const newSearchQuery = setSearchQuery(value);
              debouncedOnChange({
                ...props.value,
                searchFilter: astToString(newSearchQuery),
              });
            } else {
              const newQuery = setQuery(value);
              debouncedOnChange({
                ...props.value,
                filter: astToString(newQuery),
              });
            }
          }}
          stats={props.stats}
          feedbackUrlsByOperand={feedbackUrlsByOperand}
          setFeedbackUrlsByOperand={setFeedbackUrlsByOperand}
          updateEveryChange={props.updateEveryChange}
          readOnly={props.readOnly}
        />

        {!props.readOnly &&
          (queryOrSearchQuery.length > 0 || props.disableAutoExpand) && (
            <AddFilterButton
              variant="v2"
              onClick={() => {
                if (experimentalSearchEnabled) {
                  const newSearchQueryAst = setSearchQuery([
                    ...searchQuery,
                    { comparisons: [{}] },
                  ]);
                  onChange({
                    ...props.value,
                    searchFilter: astToString(newSearchQueryAst),
                  });
                } else {
                  const newQueryAst = setQuery([
                    ...query,
                    { comparisons: [{}] },
                  ]);
                  onChange({
                    ...props.value,
                    filter: astToString(newQueryAst),
                  });
                }
              }}
              hasFilters={queryOrSearchQuery.length > 0}
              hideFilterTitle={Boolean(props.hideFilterTitle)}
            />
          )}
      </div>
      {/* Experimental Search toggle - only shown when both props are defined */}
      {expFilteringEnabledForTenantOrDeployment &&
        !props.simpleFiltersOnly &&
        props.onExperimentalSearchToggle !== undefined &&
        !props.readOnly && (
          <div className=" flex items-center justify-between border-b border-secondary p-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Experimental Search</span>
              <Tooltip title="Use experimental full-text search capabilities">
                <div>
                  <InfoCircleIcon className="h-4 w-4 text-tertiary" />
                </div>
              </Tooltip>
            </div>
            <div className="flex items-center">
              <Switch
                size="sm"
                checked={experimentalSearchEnabled}
                onChange={(checked) =>
                  props.onExperimentalSearchToggle?.(checked)
                }
                label=""
              />
            </div>
          </div>
        )}
      {showAdvanced &&
        !props.simpleFiltersOnly &&
        !experimentalSearchEnabled && (
          <div className="mt-3 flex flex-col gap-3 border-t border-secondary px-3 pt-3">
            <span className="py font-medium">Advanced Filters</span>

            <div className="flex flex-col gap-2 rounded-md border border-tertiary p-3">
              <div className="flex items-center gap-2">
                <span>Trace filters</span>
                <Tooltip title="Include a run if filter matches for the root run of the trace">
                  <span>
                    <InfoCircleIcon className="h-3.5 w-3.5 text-tertiary" />
                  </span>
                </Tooltip>
              </div>

              {!traceFilterEnabled && !props.readOnly && (
                <div className="mt-1">
                  <ExpandableErrorAlert
                    error="Cannot add trace filters when 'Is Trace is true' filter is present"
                    color="warning"
                  />
                </div>
              )}

              <FilterBarOperandsV2
                fields={runFields.filter(
                  (field) => !('id' in field && field.id === 'is_root')
                )}
                operandsWithFields={traceQuery}
                handleChange={(value) => {
                  const newQuery = setTraceQuery(value);
                  debouncedOnChange({
                    ...props.value,
                    traceFilter: astToString(newQuery),
                  });
                }}
                stats={props.stats}
                updateEveryChange={props.updateEveryChange}
                readOnly={props.readOnly}
              />

              {!props.readOnly && (
                <AddFilterButton
                  variant="v2"
                  onClick={() => {
                    debouncedOnChange({
                      ...props.value,
                      traceFilter: astToString(
                        setTraceQuery([...traceQuery, { comparisons: [{}] }])
                      ),
                    });
                  }}
                  hasFilters={traceQuery.length > 0}
                  hideFilterTitle={false}
                />
              )}
            </div>

            <div className="flex flex-col gap-2 rounded-md border border-tertiary p-3">
              <div className="flex items-center gap-2">
                <span>Tree filters</span>
                <Tooltip title="Include a run if filter matches for any run in a trace">
                  <span>
                    <InfoCircleIcon className="h-3.5 w-3.5 text-tertiary" />
                  </span>
                </Tooltip>
              </div>

              <FilterBarOperandsV2
                fields={runFields.filter(
                  (field) => !('id' in field && field.id === 'is_root')
                )}
                operandsWithFields={treeQuery}
                handleChange={(value) => {
                  const newQuery = setTreeQuery(value);
                  debouncedOnChange({
                    ...props.value,
                    treeFilter: astToString(newQuery),
                  });
                }}
                stats={props.stats}
                updateEveryChange={props.updateEveryChange}
                readOnly={props.readOnly}
              />

              {!props.readOnly && (
                <AddFilterButton
                  variant="v2"
                  onClick={() => {
                    debouncedOnChange({
                      ...props.value,
                      treeFilter: astToString(
                        setTreeQuery([...treeQuery, { comparisons: [{}] }])
                      ),
                    });
                  }}
                  hasFilters={treeQuery.length > 0}
                  hideFilterTitle={false}
                />
              )}
            </div>
          </div>
        )}

      {!props.readOnly &&
        ((showAdvanced && !props.hideAIQuery) || experimentalSearchEnabled ? (
          <div className="flex flex-col gap-2 px-3 pt-3">
            <span>Raw Query</span>
            <FilterBarRawInput
              onSuccess={(newQuery) => {
                const queryAst = flattenAst(stringToAst(newQuery)) ?? null;
                if (!queryAst) return false;

                if (experimentalSearchEnabled) {
                  const newSearchQueryAst = setSearchQuery([
                    ...searchQuery,
                    { comparisons: queryAst.operands },
                  ]);

                  debouncedOnChange({
                    ...props.value,
                    searchFilter: astToString(newSearchQueryAst),
                  });
                } else {
                  const newQueryAst = setQuery([
                    ...query,
                    { comparisons: queryAst.operands },
                  ]);

                  debouncedOnChange({
                    ...props.value,
                    filter: astToString(newQueryAst),
                  });
                }

                return true;
              }}
            />
          </div>
        ) : (
          generateAiQueryEnabled &&
          !props.hideAIQuery &&
          !experimentalSearchEnabled && (
            <div className="flex flex-col gap-2 px-3 pt-3">
              <div className="flex flex-row justify-between">
                <span>AI Query</span>
                <EvaluatorSecrets
                  apiKeys={[EProviderSecretName.OPENAI]}
                  openSecretsRef={openSecretsRef}
                  infoMessage="Your API keys are stored encrypted on our servers."
                  triggerComponent={
                    <div className="my-auto flex cursor-pointer flex-row gap-1 rounded-md p-1 text-sm hover:bg-secondary">
                      <Key01Icon className="size-4" />
                    </div>
                  }
                />
              </div>

              <FilterBarGeneratedInput
                onSuccess={(newQuery, feedbackUrls) => {
                  const queryAst = flattenAst(stringToAst(newQuery)) ?? null;
                  if (feedbackUrls?.valid_filter) {
                    fetch(
                      `${feedbackUrls.valid_filter}?score=${queryAst ? 1 : 0}`
                    );
                    delete feedbackUrls.valid_filter;
                  }
                  if (!queryAst) {
                    return false;
                  }

                  const newQueryAst = setQuery([
                    ...query,
                    { comparisons: queryAst.operands },
                  ]);

                  onChange({
                    ...props.value,
                    filter: astToString(newQueryAst),
                  });

                  if (feedbackUrls) {
                    props.setFeedbackUrls?.(feedbackUrls);
                    setFeedbackUrlsByOperand(
                      queryAst.operands.reduce<
                        Record<string, Record<string, string>>
                      >((urlsByOp, op) => {
                        const str = astToString(op);
                        if (str) urlsByOp[str] = feedbackUrls;
                        return urlsByOp;
                      }, {})
                    );
                  }
                  return true;
                }}
                onSubmit={() => {
                  if (missingSecrets()) {
                    openSecretsRef.current?.();
                    return;
                  }
                }}
              />
            </div>
          )
        ))}
      {!props.readOnly && shouldRenderShowAdvancedButton && (
        <div className="flex items-center justify-between p-3">
          <button
            type="button"
            className="text-sm text-tertiary underline"
            onClick={() => setShowAdvanced((state) => !state)}
          >
            {getShowAdvancedButtonText()}
          </button>
        </div>
      )}
    </div>
  );
}

const variantStyles = {
  'run-details': {
    textVariant: 'xs',
    zeroFiltersText: 'Filter',
  },
  runs: {
    textVariant: 'sm',
    zeroFiltersText: 'Filters',
  },
  form: {
    textVariant: 'sm',
    zeroFiltersText: 'Filters',
  },
} as const;

export function FilterBar(props: {
  stats: RunStatsSchema | undefined;

  variant: 'runs' | 'form' | 'run-details';
  value: SearchModel;
  onChange: (value: SearchModel) => void;

  feedbackUrls?: Record<string, string>;
  setFeedbackUrls?: (urls: Record<string, string>) => void;

  onViewChange?: (
    view: 'llm' | 'trace' | 'errors' | string,
    maybeFilter?: string,
    maybeTraceFilter?: string,
    maybeTreeFilter?: string,
    experimentalSearchEnabled?: boolean
  ) => void;

  onExperimentalSearchToggle?: (enabled: boolean) => void;
  hideAIQuery?: boolean;
  alertFilteringEnabled?: boolean;
  disabledAlertRunFields?: FilterLabel[];
  className?: string;
}) {
  const searchEnabled = useInstanceFlagOrDefault(
    InstanceFlags.search_enabled
  ) as boolean;
  const runFields = getRunFields({
    searchEnabled: searchEnabled,
  });

  const nActive = useMemo(
    () => getActiveFieldsCount(props.value, runFields),
    [props.value]
  );
  const variantStyle = variantStyles[props.variant];

  const { open, setOpen } = useFilterBarOpen(props.variant);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outlined"
          color={nActive > 0 ? 'primary' : 'secondary'}
          size="sm"
          leftDecorator={FilterLinesIcon}
        >
          {nActive === 0 ? (
            <Text variant={variantStyle.textVariant}>
              {variantStyle.zeroFiltersText}
            </Text>
          ) : (
            <Text variant={variantStyle.textVariant}>
              {
                {
                  one: `1 filter`,
                  two: `${nActive} filters`,
                  few: `${nActive} filters`,
                  other: `${nActive} filters`,
                }[pluralRules.select(nActive)]
              }
            </Text>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align={props.variant === 'form' ? 'end' : 'start'}
        className={cn('z-20 w-auto min-w-[300px] overflow-hidden p-0')}
        collisionPadding={10}
        side={'bottom'}
      >
        <FilterDialogContent
          stats={props.stats}
          value={props.value}
          onChange={props.onChange}
          simpleFiltersOnly={props.variant === 'run-details'}
          showSaveFilters={props.variant === 'runs'}
          setFeedbackUrls={props.setFeedbackUrls}
          onViewChange={props.onViewChange}
          setFilterBarOpen={setOpen}
          onExperimentalSearchToggle={props.onExperimentalSearchToggle}
          hideAIQuery={props.hideAIQuery}
          disabledAlertRunFields={props.disabledAlertRunFields}
          alertFilteringEnabled={props.alertFilteringEnabled}
        />
      </PopoverContent>
    </Popover>
  );
}

function RunDetailsFilterDialogContent(
  props: Omit<FilterDialogContentProps, 'stats'> & { rootRuns: RunSchema[] }
) {
  const { conversationId: paramConversationId } = useParams();
  const [searchParams] = useSearchParams();
  const conversationId =
    paramConversationId ?? searchParams.get('conversationId');

  const statsParams =
    props.rootRuns.length === 1
      ? { trace: props.rootRuns[0].id, session: [props.rootRuns[0].session_id] }
      : {
          filter: `and(in(metadata_key, ["session_id","conversation_id","thread_id"]), eq(metadata_value, "${conversationId}"))`,
        };

  const stats = useRunStats(statsParams);

  return <FilterDialogContent {...props} stats={stats.data} />;
}

export function RunDetailsFilterBar(props: {
  rootRuns: RunSchema[];
  value: SearchModel;
  onChange: (value: SearchModel) => void;
  disabled?: boolean;
  buttonVariant?: 'button' | 'inline';
}) {
  const [open, setOpen] = useState(false);
  const searchEnabled = useInstanceFlagOrDefault(
    InstanceFlags.search_enabled
  ) as boolean;
  const runFields = getRunFields({
    searchEnabled: searchEnabled,
  });

  const nActive = useMemo(
    () => getActiveFieldsCount(props.value, runFields),
    [props.value]
  );

  const trigger =
    props.buttonVariant === 'button' ? (
      <IconButton
        variant="outlined"
        color={nActive > 0 ? 'primary' : 'secondary'}
        size="sm"
        icon={FilterLinesIcon}
        label="Filter runs"
      />
    ) : (
      <div className="flex cursor-pointer items-center gap-1 rounded-md px-2 py-1.5 hover:bg-secondary-hover">
        <FilterCountText size="md" nActive={nActive} />
      </div>
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>

      <PopoverContent
        align="start"
        className="z-10 w-auto min-w-[300px] overflow-hidden p-0"
        avoidCollisions={false}
        side={props.buttonVariant === 'button' ? 'bottom' : 'right'}
      >
        <RunDetailsFilterDialogContent
          rootRuns={props.rootRuns}
          value={props.value}
          onChange={props.onChange}
          simpleFiltersOnly
          showSaveFilters={false}
          setFeedbackUrls={undefined}
          onViewChange={undefined}
          setFilterBarOpen={setOpen}
        />
      </PopoverContent>
    </Popover>
  );
}

function FilterCountText(props: { size: 'sm' | 'md'; nActive: number }) {
  const { size, nActive } = props;

  if (size === 'sm') {
    return null;
  }

  if (nActive === 0) {
    return (
      <Text variant="sm">{variantStyles['run-details'].zeroFiltersText}</Text>
    );
  }

  const pluralText = xCount('filter', nActive);

  return <Text variant="sm">{pluralText}</Text>;
}
